import type { MailboxMessage } from '@compass/client/types/mailbox';
import { GenericTable } from '@components/GenericTable';
import { Mail, Trash2, UserRoundCheck } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

import { ErrorContainer } from '@/components/ErrorContainer';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDeepLinkNavigation } from '@/hooks/useDeepLinkNavigation';
import {
  useBulkDeleteMailbox,
  useMailboxInbox,
  useMailboxNotifications,
  useMailboxUnreadCount,
  useUpdateMailboxFlags,
} from '@/hooks/useMailbox';
import { cn } from '@/lib/utils';
import { useStore } from '@/store';
import type { TableConfig } from '@/types/table';
import { getRelativeTime } from '@/utils/date';

const PAGE_SIZE = 20;

type ReadFilter = 'unread' | 'read';

export function MailboxContainer() {
  const { navigateToDeepLink } = useDeepLinkNavigation();
  const { getTimeZone } = useStore();
  const timeZone = getTimeZone();
  const [activeTab, setActiveTab] = useState<ReadFilter>('unread');
  const [cursor, setCursor] = useState<string | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Reset cursor, page, and selection when switching tabs
  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as ReadFilter);
    setCursor(undefined);
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, []);

  const { data, isLoading, error } = useMailboxInbox({
    limit: PAGE_SIZE,
    start_after_id: cursor,
    is_read: activeTab === 'read',
  });
  const { mutate: updateFlags } = useUpdateMailboxFlags();
  const { mutate: bulkDelete, isPending: isDeleting } = useBulkDeleteMailbox();

  // Unread count from the API for the tab badge
  const { data: unreadData } = useMailboxUnreadCount();
  const unreadCount = unreadData?.unread_count ?? 0;

  // Open SSE connection for real-time inbox updates (cache-only, no API calls).
  useMailboxNotifications(true);

  const messages = useMemo(() => data?.messages ?? [], [data?.messages]);
  const hasMore = data?.has_more ?? false;

  const handleToggleRead = useCallback(
    (e: React.MouseEvent, msg: MailboxMessage) => {
      e.stopPropagation();
      if (msg.id) {
        updateFlags({ messageId: msg.id, isRead: !msg.is_read });
      }
    },
    [updateFlags],
  );

  const handleOpenMessage = useCallback(
    (msg: MailboxMessage) => {
      if (!msg.id) return;
      if (!msg.is_read) {
        updateFlags({ messageId: msg.id, isRead: true });
      }
      navigateToDeepLink((msg.metadata ?? {}) as Record<string, unknown>);
    },
    [updateFlags, navigateToDeepLink],
  );

  const handleDeleteSelected = useCallback(() => {
    if (selectedIds.size === 0) return;
    bulkDelete([...selectedIds], {
      onSuccess: () => setSelectedIds(new Set()),
    });
  }, [selectedIds, bulkDelete]);

  const handleSelectionChange = useCallback((newSelection: Set<string>) => {
    setSelectedIds(newSelection);
  }, []);

  const handleSelectAllCurrentPage = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedIds(new Set(messages.map(m => m.id ?? '').filter(Boolean)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [messages],
  );

  const tableConfig = useMemo((): TableConfig<MailboxMessage> => {
    return {
      columns: [
        {
          key: 'status',
          header: '',
          accessor: item => (
            <span className="flex items-center justify-center">
              <span
                className={cn(
                  'flex items-center justify-center size-8 rounded-lg',
                  item.is_read ? 'bg-muted' : 'bg-primary/10',
                )}
              >
                <UserRoundCheck
                  className={cn('size-4', item.is_read ? 'text-muted-foreground' : 'text-primary')}
                />
              </span>
            </span>
          ),
          className: 'w-12 !bg-transparent',
        },
        {
          key: 'subject',
          header: 'Subject',
          accessor: item => (
            <button
              type="button"
              className="text-left hover:underline"
              onClick={() => handleOpenMessage(item)}
            >
              <span
                className={cn(
                  'whitespace-normal break-words',
                  item.is_read ? 'text-muted-foreground' : 'font-medium text-foreground',
                )}
              >
                {item.subject || '(No subject)'}
              </span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                {getRelativeTime(item.created_at, timeZone)}
              </span>
            </button>
          ),
          className: '!whitespace-normal',
        },
        {
          key: 'actions',
          header: '',
          accessor: item => (
            <Button
              variant="outline"
              size="xs"
              className="whitespace-nowrap"
              onClick={e => handleToggleRead(e, item)}
              aria-label={item.is_read ? 'Mark message as unread' : 'Mark message as read'}
            >
              {item.is_read ? 'Mark as unread' : 'Mark as read'}
            </Button>
          ),
          className: 'w-36',
        },
      ],
      getRowKey: item => item.id || '',
      getRowClassName: () => '',
      emptyState: {
        icon: <Mail className="h-10 w-10 text-muted-foreground" />,
        title: activeTab === 'unread' ? 'No unread messages' : 'No read messages',
        description:
          activeTab === 'unread' ? "You're all caught up!" : 'No messages have been read yet.',
      },
    };
  }, [handleToggleRead, handleOpenMessage, activeTab, timeZone]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page > 1 && messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        setCursor(lastMessage.id);
      } else {
        setCursor(undefined);
      }
      setCurrentPage(page);
      setSelectedIds(new Set()); // Clear selection on page change
    },
    [messages],
  );

  if (error) {
    return (
      <>
        <BreadcrumbPortal>
          <Breadcrumb>
            <Breadcrumb.Item>Notifications</Breadcrumb.Item>
          </Breadcrumb>
        </BreadcrumbPortal>
        <div className="max-w-4xl mx-auto px-6 pt-12">
          <ErrorContainer errorMessage="Failed to load notifications" />
        </div>
      </>
    );
  }

  return (
    <>
      <BreadcrumbPortal>
        <Breadcrumb>
          <Breadcrumb.Item>Notifications</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbPortal>

      <div className="h-full flex flex-col overflow-hidden">
        <div className="max-w-4xl mx-auto w-full px-6 pt-8 pb-2">
          {/* Page Header */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold">Notifications</h1>
          </div>

          {/* Tabs + bulk actions toolbar */}
          <div className="flex items-center justify-between mb-4">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList>
                <TabsTrigger value="unread">
                  Unread{unreadCount > 0 && ` (${unreadCount})`}
                </TabsTrigger>
                <TabsTrigger value="read">Read</TabsTrigger>
              </TabsList>
            </Tabs>

            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={isDeleting}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Table fills remaining height and scrolls internally */}
        <div className="flex-1 min-h-0 max-w-4xl mx-auto w-full px-6 pb-4 flex flex-col">
          <GenericTable
            config={tableConfig}
            data={messages}
            loading={isLoading}
            currentPage={currentPage}
            hasNextPage={hasMore}
            onPageChange={handlePageChange}
            selectionMode
            selectedIds={selectedIds}
            onSelectionChange={handleSelectionChange}
            onSelectAllCurrentPage={handleSelectAllCurrentPage}
          />
        </div>
      </div>
    </>
  );
}
